import React from 'react';
import PropTypes from 'prop-types';

import { theme, Tooltip } from '@ublend-npm/aulaui-next';
import { Menu, MenuItem } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

import { Edit } from '@mui/icons-material';
import { Box, IconButton, Separator, useTheme } from '@ublend-npm/aulaui-core';

import { API_URL, FILE_URL } from '../../../../../../core/constants/endpoints';

import ChangeGroupNameModal from './modals/RenameGroupModal.container';
import EditMessageModal from './modals/EditMessageModalAulaEditor.container';
import ParticipantsModal from './modals/ViewParticipantsModal.container';
import LeaveConversationModal from './LeaveConversationModal';
import {
  MessageTable,
  WriteZoneContainer,
  ActionContainer,
} from './ConversationModal.styled';

import ConversationModalParticipants from './ConversationModalParticipants.container';
import ConversationModalResponsiveWrapper from './ConversationModalResponsiveWrapper';

import * as types from '../types';
import onlyWhenDefined from './onlyWhenDefined';
import WriteZone from './WriteZoneAulaEditor/WriteZone.container';
import {
  INBOX_CONVERSATION_ACTION_ADD_PARTICIPANTS_RESPONSIVE,
  INBOX_CONVERSATION_ACTION_LEAVE_CONVERSATION_RESPONSIVE,
  INBOX_CONVERSATION_ACTION_RENAME_CONVERSATION_RESPONSIVE,
  INBOX_CONVERSATION_ACTION_VIEW_PARTICIPANTS,
  INBOX_CONVERSATION_ACTION_MUTE_RESPONSIVE,
  INBOX_CONVERSATION_ACTION_UNMUTE_RESPONSIVE,
  INBOX_CONVERSATION_UNARCHIVE_CONVERSATION,
  INBOX_CONVERSATION_ARCHIVE_CONVERSATION_RESPONSIVE,
} from '../../../../../constants/texts';
import EmojiPicker from '../../../emoji/EmojiPicker';
import ConversationModalTitle from './ConversationModalTitle';
import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  MESSAGE_ARCHIVED,
  MESSAGE_MUTED,
  MESSAGE_LEFT,
  MESSAGE_PARTICIPANTS_VIEWED,
} from '@core/constants/analytics';

const flow =
  (...functions) =>
  (component) =>
    functions.reduce((acc, next) => next(acc), component);

const ConversationModalParticipantsWithTheme = (props) => {
  const { palette } = useTheme();

  return (
    <ConversationModalParticipants
      {...props}
      isDarkMode={palette.mode === 'dark'}
    />
  );
};

// Functional component to handle MUI Menu with dark theme
const ConversationMenuMUI = ({ conversation, componentProps }) => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  if (!conversation) {
    return null;
  }

  const headerActions = ConversationActions?.filter((action) =>
    action.visible(componentProps)
  );

  if (headerActions.length === 0) {
    return null;
  }

  const isDark = palette.mode === 'dark';

  const handleClick = (event) => {
    // Use the separator as anchor instead of the button
    const separatorElement = document.querySelector('[data-separator-anchor]');
    setAnchorEl(separatorElement || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (handler) => {
    handler();
    handleClose();
  };

  return (
    <Box>
      <IconButton
        size="small"
        type="secondary"
        id="conversation-menu-button"
        label="Conversation menu"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            backgroundColor: isDark ? '#363636' : undefined,
            color: isDark ? '#ffffff' : undefined,
            width: '228px',
            marginTop: '4px',
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: isDark ? '#363636 !important' : undefined,
            color: isDark ? '#ffffff' : undefined,
            width: '228px',
            marginTop: '4px',
          },
          '& .MuiMenu-paper': {
            backgroundColor: isDark ? '#363636 !important' : undefined,
          },
          '& .MuiPopover-paper': {
            backgroundColor: isDark ? '#363636 !important' : undefined,
          },
          '& .MuiMenu-list': {
            backgroundColor: isDark ? '#363636 !important' : undefined,
          },
          '& .MuiList-root': {
            backgroundColor: isDark ? '#363636 !important' : undefined,
          },
        }}
      >
        {headerActions.map(
          ({
            getLabel,
            getHandler,
            key,
            getIcon,
            disabled,
            disabledToolTip,
          }) => {
            const Icon = getIcon(componentProps);
            const isDisabled = disabled(componentProps);
            const menuItem = (
              <MenuItem
                key={`action:${key}`}
                disabled={isDisabled}
                onClick={() => handleMenuItemClick(getHandler(componentProps))}
                sx={{
                  color: isDark ? '#ffffff' : undefined,
                  width: '228px',
                  height: '48px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: isDark ? '#D0BCFA29' : undefined,
                  },
                }}
              >
                <Icon
                  style={{
                    color: isDark ? '#FFFFFF8F' : theme.color.grey11,
                    marginRight: '12px',
                    fontSize: '20px',
                  }}
                />
                {getLabel(componentProps)}
              </MenuItem>
            );
            return isDisabled && disabledToolTip ? (
              <Tooltip title={disabledToolTip} key={`action:${key}`}>
                {menuItem}
              </Tooltip>
            ) : (
              menuItem
            );
          }
        )}
      </Menu>
    </Box>
  );
};

const ConversationActions = [
  {
    getLabel: () => INBOX_CONVERSATION_ACTION_VIEW_PARTICIPANTS,
    getHandler: ({ openViewParticipants }) => {
      return () => {
        analyticsTrackEvent(MESSAGE_PARTICIPANTS_VIEWED);
        openViewParticipants();
      };
    },
    key: 'openViewParticipants',
    getIcon: () => RemoveRedEyeIcon,
    visible: ({ conversation }) => !!(conversation && conversation.isGroup),
    disabled: () => false,
  },
  {
    getLabel: () => INBOX_CONVERSATION_ACTION_ADD_PARTICIPANTS_RESPONSIVE,
    getHandler: ({ openAddParticipant }) => openAddParticipant,
    key: 'openAddParticipants',
    getIcon: () => PersonAddIcon,
    visible: ({ conversation }) =>
      !!(
        conversation &&
        conversation.isGroup &&
        !conversation.boundToSpaceGroup
      ),
    disabled: ({ conversation }) =>
      !!(conversation && conversation.educatorOnly),
    disabledToolTip: 'Educator only conversation',
  },
  {
    getLabel: () => INBOX_CONVERSATION_ACTION_RENAME_CONVERSATION_RESPONSIVE,
    getHandler: ({ openRenameGroup }) => openRenameGroup,
    key: 'openRenameGroup',
    getIcon: () => Edit,
    visible: ({ conversation }) =>
      !!(
        conversation &&
        conversation.isGroup &&
        !conversation.boundToSpaceGroup &&
        !conversation.educatorOnly
      ),
    disabled: () => false,
  },
  {
    getLabel: ({ conversationMuted }) =>
      conversationMuted
        ? INBOX_CONVERSATION_ACTION_UNMUTE_RESPONSIVE
        : INBOX_CONVERSATION_ACTION_MUTE_RESPONSIVE,
    getHandler: ({ toggleMuteConversation, conversationMuted }) => {
      return () => {
        if (!conversationMuted) {
          analyticsTrackEvent(MESSAGE_MUTED);
        }
        toggleMuteConversation();
      };
    },
    key: 'muteUnmute',
    getIcon: ({ conversationMuted }) =>
      conversationMuted ? VolumeUpIcon : VolumeOffIcon,
    visible: ({ conversation }) => !!conversation,
    disabled: () => false,
  },
  {
    getLabel: ({ conversation }) =>
      conversation.closed
        ? INBOX_CONVERSATION_UNARCHIVE_CONVERSATION
        : INBOX_CONVERSATION_ARCHIVE_CONVERSATION_RESPONSIVE,
    getHandler: ({
      conversation,
      setConversationClosed,
      setConversationReopened,
    }) =>
      conversation.closed
        ? () => setConversationReopened(conversation.id)
        : () => {
            analyticsTrackEvent(MESSAGE_ARCHIVED);
            setConversationClosed();
          },
    key: 'archiveUnarchiveConversation',
    getIcon: ({ conversation }) => {
      return conversation.closed ? UnarchiveOutlinedIcon : ArchiveOutlinedIcon;
    },
    visible: ({ conversation }) =>
      !!(conversation && !conversation.boundToSpaceGroup),
    disabled: () => false,
  },
  {
    getLabel: () => INBOX_CONVERSATION_ACTION_LEAVE_CONVERSATION_RESPONSIVE,
    getHandler: ({ openLeaveGroup }) => openLeaveGroup,
    key: 'openLeaveGroup',
    getIcon: () => ExitToAppIcon,
    visible: ({ conversation }) =>
      !!(
        conversation &&
        conversation.isGroup &&
        !conversation.boundToSpaceGroup &&
        !conversation.educatorOnly
      ),
    disabled: () => false,
  },
];

class ConversationModal extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      emojiPickerAnchor: null,
      emojiPickerOpenForMessageId: null,
    };

    this.messageTable = null;
    this.writeZone = null;
    this.conversationModalId = 'conversation-panel';

    this.closeLeaveGroup = this.closeLeaveGroup.bind(this);
    this.focusWriteZone = this.focusWriteZone.bind(this);
    this.onWriteZoneResize = this.onWriteZoneResize.bind(this);
    this.onMessageSubmit = this.onMessageSubmit.bind(this);
    this.setMessageTable = this.setMessageTable.bind(this);
    this.setWriteZone = this.setWriteZone.bind(this);
    this.openEmojiPicker = this.openEmojiPicker.bind(this);
    this.closeEmojiPicker = this.closeEmojiPicker.bind(this);
    this.selectEmoji = this.selectEmoji.bind(this);
    this.onJumpToBottom = this.onJumpToBottom.bind(this);
    this.onFocusWindow = this.onFocusWindow.bind(this);
    this.handleLeaveConversation = this.handleLeaveConversation.bind(this);

    this.editorConfig = {
      filesUrl: FILE_URL(),
      apiUrl: API_URL(),
    };
  }

  async componentDidMount() {
    window.addEventListener('focus', this.onFocusWindow);
    const { conversation, setConversationRead } = this.props;

    // Mark conversation as read when opened
    if (setConversationRead) {
      setConversationRead();
    }

    // Fetch notification settings if not already fetched
    if (!hasFetchedNotificationSettings && fetchNotificationSettings) {
      fetchNotificationSettings();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('focus', this.onFocusWindow);
  }

  onFocusWindow() {
    this.focusWriteZone();

    // Mark conversation as read when window gains focus
    const { setConversationRead } = this.props;
    if (setConversationRead) {
      setConversationRead();
    }
  }

  onMessageSubmit() {
    this.messageTable.scrollToBottom();
  }

  onWriteZoneResize() {
    if (!this.messageTable) return;
    this.messageTable.onWriteZoneResize();
  }

  async onJumpToBottom() {
    const { fetchMessages } = this.props;
    await fetchMessages();
    this.messageTable.scrollToBottom();
  }

  setMessageTable(reference) {
    this.messageTable = reference;
  }

  setWriteZone(reference) {
    this.writeZone = reference;
  }

  closeLeaveGroup() {
    const { closeLeaveGroup } = this.props;
    closeLeaveGroup();
    this.focusWriteZone();
  }

  focusWriteZone() {
    if (!this.writeZone) return;
    this.writeZone.focus();
  }

  openEmojiPicker({ messageId, anchor }) {
    this.setState({
      emojiPickerOpenForMessageId: messageId,
      emojiPickerAnchor: anchor,
    });
  }

  closeEmojiPicker() {
    this.setState({
      emojiPickerOpenForMessageId: null,
      emojiPickerAnchor: null,
    });
  }

  selectEmoji(emoji) {
    const { toggleReaction } = this.props;
    const { emojiPickerOpenForMessageId } = this.state;
    toggleReaction({
      itemId: emojiPickerOpenForMessageId,
      emojiName: emoji.short_name,
    });
  }

  handleLeaveConversation() {
    // Track analytics event for leaving conversation
    analyticsTrackEvent(MESSAGE_LEFT);
    this.props.leaveConversation();
  }

  render() {
    const {
      title,
      status,
      conversation,
      currentUser,
      openViewParticipants,
      leavingConversation,
      leaveGroupOpened,
      leaveConversation,
      conversationMuted,
      viewParticipantsOpened,
      addParticipantOpened,
    } = this.props;

    const { emojiPickerAnchor } = this.state;

    return (
      <Box>
        <ConversationModalResponsiveWrapper
          viewParticipantsOpened={viewParticipantsOpened}
          addParticipantOpened={addParticipantOpened}
        >
          <Box p="24px" id="message-conatiner" sx={{ position: 'relative' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Box display="flex" gap="8px" alignItems="center">
                  {/* For individual conversations, show avatar before title */}
                  {!conversation.isGroup && (
                    <Box marginRight="2px">
                      <ConversationModalParticipantsWithTheme
                        conversation={conversation}
                        currentUser={currentUser}
                        openViewParticipants={openViewParticipants}
                      />
                    </Box>
                  )}
                  <ConversationModalTitle
                    title={title}
                    status={status}
                    conversationMuted={conversationMuted}
                  />
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap="0.5rem">
                {/* For group conversations, show avatars after title and before menu */}
                {conversation.isGroup && (
                  <Box marginRight="0px">
                    <ConversationModalParticipantsWithTheme
                      conversation={conversation}
                      currentUser={currentUser}
                      openViewParticipants={openViewParticipants}
                    />
                  </Box>
                )}
                {/* <ConversationModalTitle
                  title={title}
                  status={status}
                  conversationMuted={conversationMuted}
                /> */}

                <ActionContainer id={`menu_${conversation.id}`}>
                  <ConversationMenuMUI
                    conversation={conversation}
                    componentProps={this.props}
                  />
                </ActionContainer>
              </Box>
            </Box>

            <Separator sx={{ my: '16px' }} data-separator-anchor />
            <Box
              sx={{
                position: 'relative',
                height: '70vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                width: '100%',
              }}
              tabIndex={-1}
            >
              <MessageTable
                innerRef={this.setMessageTable}
                openEmojiPicker={this.openEmojiPicker}
                closeEmojiPicker={this.closeEmojiPicker}
              />
              <WriteZoneContainer>
                <WriteZone
                  editorConfig={this.editorConfig}
                  ref={this.setWriteZone}
                  onHeightChange={this.onWriteZoneResize}
                  onMessageSubmit={this.onMessageSubmit}
                  htmlAttributes={{
                    'data-testid': 'dm-message-textbox',
                  }}
                />
              </WriteZoneContainer>
            </Box>
          </Box>
        </ConversationModalResponsiveWrapper>
        {viewParticipantsOpened && (
          <ParticipantsModal
            conversation={conversation}
            currentUser={currentUser}
            onClose={this.focusWriteZone}
          />
        )}
        <ChangeGroupNameModal
          conversation={conversation}
          currentUser={currentUser}
          onClose={this.focusWriteZone}
          title={title}
        />

        <LeaveConversationModal
          loading={leavingConversation}
          open={leaveGroupOpened}
          onClose={this.closeLeaveGroup}
          onConfirm={this.handleLeaveConversation}
          title={title}
        />
        <EditMessageModal
          editorConfig={this.editorConfig}
          conversation={conversation}
          onClose={this.focusWriteZone}
        />
        <EmojiPicker
          id="conversations-emoji-picker"
          anchorEl={emojiPickerAnchor}
          show={!!emojiPickerAnchor}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          onClose={this.closeEmojiPicker}
          onSelect={this.selectEmoji}
        />
      </Box>
    );
  }
}

ConversationModal.propTypes = {
  groupId: PropTypes.string.isRequired,
  setConversationReopened: PropTypes.func.isRequired,
  setConversationRead: PropTypes.func.isRequired,
  closeLeaveGroup: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  conversation: types.Conversation.isRequired,
  currentUser: types.User.isRequired,
  leaveConversation: PropTypes.func.isRequired,
  leavingConversation: PropTypes.bool,
  leaveGroupOpened: PropTypes.bool,
  open: PropTypes.bool,
  openAddParticipant: PropTypes.func.isRequired,
  openLeaveGroup: PropTypes.func.isRequired,
  openRenameGroup: PropTypes.func.isRequired,
  openViewParticipants: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  status: types.Status,
  toggleReaction: PropTypes.func.isRequired,
  fetchMessages: PropTypes.func.isRequired,
  conversationMuted: PropTypes.bool.isRequired,
  toggleMuteConversation: PropTypes.func.isRequired,
  hasFetchedNotificationSettings: PropTypes.bool,
  fetchNotificationSettings: PropTypes.func,
  viewParticipantsOpened: PropTypes.bool,
  addParticipantOpened: PropTypes.bool,
};

ConversationModal.defaultProps = {
  open: false,
  status: null,
  leaveGroupOpened: false,
  leavingConversation: false,
  hasFetchedNotificationSettings: false,
  fetchNotificationSettings: null,
  viewParticipantsOpened: false,
  addParticipantOpened: false,
};

const wrap = flow(onlyWhenDefined(['conversation', 'currentUser']));

export default wrap(ConversationModal);
